<script>
  export let question;
  export let updateWorkingQuestion;

  let isNodeProps, targetQuestion, fieldLabelsValue, availableQuestions;
  
  function isNodePropsQuestion() {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset.some((item) => item[0] === "nodeProps");
  }
  
  function getTargetQuestion() {
    const dataset = question.dataset || [];
    const nodePropsHandler = dataset.find(handler => handler[0] === 'nodeProps');
    
    if (nodePropsHandler && nodePropsHandler[1] !== undefined) {
      return nodePropsHandler[1]; // Target question number
    }
    
    return 0;
  }
  
  function updateTargetQuestion(newTarget) {
    const dataset = [...(question.dataset || [])];
    const nodePropsHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodeProps');
    
    if (nodePropsHandlerIndex !== -1) {
      const handler = [...dataset[nodePropsHandlerIndex]];
      handler[1] = parseInt(newTarget);
      dataset[nodePropsHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
      
      // Auto-update fieldLabels to match the target question
      if (!question.manyToMany) {
        updateWorkingQuestion('fieldLabels', `__answers${newTarget}__`);
      }
    }
  }
  
  function getFieldLabelsValue() {
    if (question.manyToMany) {
      return question.fieldLabels || '__priorAnswers__';
    } else {
      const targetQuestion = getTargetQuestion();
      return question.fieldLabels || `__answers${targetQuestion}__`;
    }
  }
  
  function updateFieldLabels(value) {
    updateWorkingQuestion('fieldLabels', value);
  }
  
  function getAvailableQuestions() {
    const currentQuestionIndex = parseInt(question.id?.split('--')[1]) || 0;
    const questions = [];
    
    for (let i = 0; i < currentQuestionIndex; i++) {
      questions.push({
        index: i,
        label: `Question ${i}`,
        id: `question--${i}`
      });
    }
    
    return questions;
  }
  
  // Reactive derived state - automatically updates when question changes
  $: question, isNodeProps = isNodePropsQuestion();
  $: question, targetQuestion = getTargetQuestion();
  $: question, fieldLabelsValue = getFieldLabelsValue();
  $: question, availableQuestions = getAvailableQuestions();
</script>

{#if isNodeProps}
  <div class="nodeprops-connection-section">
    <h5>Node Props Connection</h5>
    <p class="section-help">
      This question adds properties to nodes created by another question.
    </p>
    
    <div class="connection-grid">
      <div class="field-wrapper">
        <label>Target Question</label>
        <select 
          value={targetQuestion}
          on:change={(e) => updateTargetQuestion(e.target.value)}
          class="target-select"
        >
          {#each availableQuestions as q}
            <option value={q.index}>Question {q.index}</option>
          {/each}
        </select>
        <p class="field-help">
          Which question created the nodes you want to add properties to
        </p>
      </div>
      
      <div class="field-wrapper">
        <label>Field Labels</label>
        {#if question.manyToMany}
          <select 
            value={fieldLabelsValue}
            on:change={(e) => updateFieldLabels(e.target.value)}
            class="fieldlabels-select"
          >
            <option value="__priorAnswers__">Previous Question (All)</option>
            <option value="__priorAnswer__">Previous Question (Single)</option>
            {#each availableQuestions as q}
              <option value="__answers{q.index}__">Question {q.index} (All)</option>
              <option value="__answer{q.index}__">Question {q.index} (Single)</option>
            {/each}
          </select>
        {:else}
          <div class="auto-fieldlabels">
            <span class="auto-value">__answers{targetQuestion}__</span>
            <span class="auto-badge">AUTO</span>
          </div>
        {/if}
        <p class="field-help">
          {#if question.manyToMany}
            Choose which question's answers to connect to (many-to-many mode)
          {:else}
            Automatically set to match target question answers
          {/if}
        </p>
      </div>
    </div>
    
    <div class="connection-summary">
      <h6>Connection Summary</h6>
      <p>
        <strong>Adding properties to:</strong> Nodes from Question {targetQuestion}<br/>
        <strong>Field labels source:</strong> {fieldLabelsValue}<br/>
        <strong>Many-to-many:</strong> {question.manyToMany ? 'Yes' : 'No'}
      </p>
    </div>
  </div>
{/if}

<style lang="scss">
  .nodeprops-connection-section {
    border: 2px solid #f59e0b;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
    background: #fef3c7;
    
    h5 {
      margin: 0 0 8px 0;
      font-size: 0.85rem;
      font-weight: bold;
      color: #92400e;
    }
    
    .section-help {
      font-size: 0.7rem;
      color: #78350f;
      margin-bottom: 15px;
      font-style: italic;
    }
  }
  
  .connection-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 15px;
  }
  
  .field-wrapper {
    label {
      display: inline-flex;
      font-weight: bold;
      margin-bottom: 5px;
      font-size: 0.75rem;
      color: #92400e;
    }
    
    select {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #f59e0b;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      font-family: monospace;
      
      &:focus {
        outline: none;
        border-color: #d97706;
        box-shadow: 0 0 0 1px #d97706;
      }
    }
  }
  
  .auto-fieldlabels {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    
    .auto-value {
      font-family: monospace;
      font-size: 0.75rem;
      color: #374151;
    }
    
    .auto-badge {
      background: #6b7280;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 9px;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
  }
  
  .connection-summary {
    background: #fff;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    padding: 12px;
    
    h6 {
      margin: 0 0 8px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #92400e;
    }
    
    p {
      margin: 0;
      font-size: 0.7rem;
      color: #78350f;
      line-height: 1.4;
    }
  }
  
  .field-help {
    font-size: 11px;
    color: #78350f;
    margin-top: 5px;
    font-style: italic;
  }
</style>